import type { OrderItem } from "./api";

export function getOrderAmountPaid(order: Pick<OrderItem, "amountPaid">): number {
  return typeof order.amountPaid === "number" && Number.isFinite(order.amountPaid)
    ? order.amountPaid
    : 0;
}

export function getOrderTotal(order: Pick<OrderItem, "totals">): number {
  return typeof order.totals.total === "number" && Number.isFinite(order.totals.total)
    ? order.totals.total
    : 0;
}

export function getOrderBalanceDue(
  order: Pick<OrderItem, "amountDue" | "balanceDue" | "amountPaid" | "totals">,
): number {
  const explicit =
    typeof order.amountDue === "number" && Number.isFinite(order.amountDue)
      ? order.amountDue
      : order.balanceDue;
  if (typeof explicit === "number" && Number.isFinite(explicit)) {
    return Math.max(0, explicit);
  }
  return Math.max(0, getOrderTotal(order) - getOrderAmountPaid(order));
}

export function isPartialPaymentOrder(order: OrderItem): boolean {
  return (
    order.status === "partially_paid" ||
    (order.accessDecision === "partial" && getOrderBalanceDue(order) > 0)
  );
}

export function getOrderNumber(order: OrderItem): string {
  return order.orderNumber || order._id.slice(-8).toUpperCase();
}

export function getOrderItemsSummary(
  order: OrderItem,
  formatMore: (title: string, count: number) => string,
): string {
  const firstTitle = order.items[0]?.titleSnapshot?.trim();
  const title = firstTitle || getOrderNumber(order);
  const remaining = Math.max(0, order.items.length - 1);
  return remaining > 0 ? formatMore(title, remaining) : title;
}

export function findPartialOrderById(
  orders: OrderItem[] | undefined,
  orderId: string | undefined | null,
): OrderItem | undefined {
  if (!orderId) return undefined;
  return orders?.find((order) => order._id === orderId || order.orderNumber === orderId);
}

export function findPartialOrderForCourse(
  orders: OrderItem[] | undefined,
  courseId: string | undefined | null,
): OrderItem | undefined {
  if (!courseId) return undefined;
  return orders?.find(
    (order) =>
      isPartialPaymentOrder(order) &&
      order.items.some((item) => item.type === "course" && item.refId === courseId),
  );
}

export function findPartialOrderForCatalogRef(
  orders: OrderItem[] | undefined,
  refId: string | undefined | null,
  type: "course" | "digital",
): OrderItem | undefined {
  if (!refId) return undefined;
  return orders?.find(
    (order) =>
      isPartialPaymentOrder(order) &&
      order.items.some((item) => item.type === type && item.refId === refId),
  );
}
